import { defaultChannelsStore } from '../store/defaultChannelsStore';
import { getCurrentDatasetTimezone } from '../store/datasetTimezoneStore';
import { buildColorGrouping, calculateVmgIntervals } from './colorGrouping';
import { localTimeInTimezoneToUtcDate } from './global';

/** Legend entry shape expected by `ScatterTimeseries` `groups` prop. */
export type ScatterTimeseriesLegendGroup = { name: string; color: string };

function readNumber(row: Record<string, unknown>, key: string): number | null {
  const direct = row[key];
  if (direct !== undefined && direct !== null && !Number.isNaN(Number(direct))) {
    return Number(direct);
  }
  const lower = key.charAt(0).toLowerCase() + key.slice(1);
  const v = row[lower] ?? row[key.toUpperCase()];
  if (v !== undefined && v !== null && !Number.isNaN(Number(v))) {
    return Number(v);
  }
  return null;
}

/** True when a datetime string carries Z or a numeric UTC offset (e.g. +01, +01:00). */
export function hasExplicitTimezoneOffset(value: string): boolean {
  const trimmed = value.trim();
  return /Z$/i.test(trimmed) || /[+-]\d{2}(?::\d{2})?$/.test(trimmed);
}

/** Normalize API datetime text to ISO-ish form for `Date` parsing. */
export function normalizeIsoishDatetimeString(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('T')) return trimmed;
  return trimmed.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
}

function splitNaiveDateTime(value: string): { dateStr: string; timeStr: string } | null {
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)/);
  if (!match) return null;
  return { dateStr: match[1], timeStr: match[2] };
}

/** Per-draw cache for scatter timeline datetime parsing. */
export type ScatterDatetimeCache = Map<string, Date | null>;

/** Cache key for a raw datetime candidate. */
export function scatterDatetimeCacheKey(raw: unknown): string {
  if (raw instanceof Date) {
    const t = raw.getTime();
    return Number.isNaN(t) ? 'd:NaN' : `d:${t}`;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return `n:${raw}`;
  }
  return `s:${String(raw).trim()}`;
}

/** Datetime field candidates for scatter timeline rows (performance + maneuver). */
export function scatterRowDatetimeCandidates(row: Record<string, unknown>): unknown[] {
  const candidates: unknown[] = [
    row.Datetime,
    row.datetime,
    row.DATETIME,
    row.start_time,
    row.Start_time,
    row.end_time,
    row.End_time,
    row.maneuver_time,
    row.Maneuver_time,
    row.date,
    row.Date,
  ];
  if (
    row.metadata != null &&
    typeof row.metadata === 'object' &&
    row.metadata !== null &&
    'datetime' in row.metadata
  ) {
    candidates.push((row.metadata as { datetime?: unknown }).datetime);
  }
  return candidates;
}

/** Row-level dataset timezone from API rows, with store fallback (axis labels only). */
export function maneuverRowTimezone(row: Record<string, unknown>): string | null {
  const fromRow =
    (typeof row.timezone === 'string' && row.timezone.trim()) ||
    (typeof row.Timezone === 'string' && row.Timezone.trim()) ||
    '';
  if (fromRow) return fromRow;
  return getCurrentDatasetTimezone();
}

/** Normalize short API offsets (+01) to ISO form (+01:00) for reliable `Date` parsing. */
function normalizeOffsetForDateParse(value: string): string {
  return value.replace(/([+-]\d{2})$/, '$1:00');
}

function parseScatterRawDatetime(raw: unknown): Date | null {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const dt = new Date(raw);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  let normalized =
    trimmed.includes(' ') && !trimmed.includes('T')
      ? trimmed.replace(/ /, 'T')
      : normalizeIsoishDatetimeString(trimmed);

  if (hasExplicitTimezoneOffset(normalized)) {
    normalized = normalizeOffsetForDateParse(normalized);
  }

  const dt = new Date(normalized);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Parse scatter timeline x-values from API `Datetime` fields.
 * Matches AdvancedScatter / `processPerformanceData`; timezone is for axis labels only.
 */
export function parseScatterDatetime(
  row: Record<string, unknown>,
  cache?: ScatterDatetimeCache
): Date | null {
  for (const raw of scatterRowDatetimeCandidates(row)) {
    if (raw == null || raw === '') continue;
    const key = scatterDatetimeCacheKey(raw);
    if (cache?.has(key)) {
      const cached = cache.get(key)!;
      if (cached) return cached;
      continue;
    }
    const instant = parseScatterRawDatetime(raw);
    cache?.set(key, instant);
    if (instant) return instant;
  }
  return null;
}

/**
 * Parse user-entered or legacy naive datetime to UTC (Events, VideoSync).
 * API rows should use `parseScatterDatetime` instead.
 */
export function parseManeuverDatetimeValue(
  raw: unknown,
  timezone?: string | null
): Date | null {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const dt = new Date(raw);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  let normalized = normalizeIsoishDatetimeString(trimmed);

  if (hasExplicitTimezoneOffset(normalized)) {
    normalized = normalizeOffsetForDateParse(normalized);
    const dt = new Date(normalized);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const tz = timezone?.trim();
  if (tz) {
    const parts = splitNaiveDateTime(normalized);
    if (parts) {
      const utc = localTimeInTimezoneToUtcDate(parts.dateStr, parts.timeStr, tz);
      if (utc) return utc;
    }
  }

  const dt = new Date(normalized);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * TWS value for maneuver selection timelines (`ScatterTimeseries` with `skipSelectedSourcesFilter`).
 * Omits BSP fallbacks so the strip stays on true wind speed.
 */
export function pickManeuverTimelineTwsValue(
  row: Record<string, unknown>,
  twsFieldName: string
): number | null {
  const candidates = [
    twsFieldName,
    twsFieldName.charAt(0).toLowerCase() + twsFieldName.slice(1),
    'Tws_kts',
    'tws_kts',
    'Tws_kph',
    'tws_kph',
    'TWS',
    'tws',
    'tws_avg',
    'Tws_avg',
    'tws_bin',
    'Tws_bin',
  ];
  for (const k of candidates) {
    const n = readNumber(row, k);
    if (n !== null && Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Maps raw maneuver table rows to timeline-ready points for `ScatterTimeseries`.
 * Output `Datetime`/`datetime` are UTC ISO strings for fast chart parsing on redraw.
 */
export function mapManeuverRowsToScatterTimeseriesPoints(
  rows: ReadonlyArray<Record<string, unknown>>
): Record<string, unknown>[] {
  const twsField = defaultChannelsStore.twsName() || 'Tws_kts';
  const out: Record<string, unknown>[] = [];
  const datetimeCache: ScatterDatetimeCache = new Map();

  for (const row of rows) {
    const rawId =
      row.event_id ??
      row.Event_id ??
      row.EVENT_ID ??
      (row as { id?: unknown }).id;
    if (rawId === undefined || rawId === null) continue;
    const id = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
    if (!Number.isFinite(id) || id <= 0) continue;

    const instant = parseScatterDatetime(row, datetimeCache);
    if (!instant) continue;

    const twsVal = pickManeuverTimelineTwsValue(row, twsField);
    if (twsVal === null) continue;

    const iso = instant.toISOString();

    out.push({
      ...row,
      event_id: id,
      Datetime: iso,
      datetime: iso,
      TWS: twsVal,
      [twsField]: twsVal,
    });
  }

  return out;
}

/** Builds `ScatterTimeseries` `groups` from current maneuver rows and color mode. */
export function maneuverTimelineLegendGroups(
  rows: ReadonlyArray<Record<string, unknown>>,
  colorName: string
): ScatterTimeseriesLegendGroup[] {
  const arr = [...rows];
  if (arr.length === 0) return [];

  const mode = String(colorName || 'TWS').toUpperCase();

  if (mode === 'VMG') {
    const vmgIntervals = calculateVmgIntervals(arr);
    if (!vmgIntervals) return [];
    const { getItemColor } = buildColorGrouping(arr, colorName);
    const intervalGroups: string[] = [];
    for (let i = 0; i < 5; i++) {
      const intervalMin = vmgIntervals.min + i * vmgIntervals.intervalSize;
      const intervalMax =
        i === 4 ? vmgIntervals.max : vmgIntervals.min + (i + 1) * vmgIntervals.intervalSize;
      intervalGroups.push(`${intervalMin.toFixed(1)}-${intervalMax.toFixed(1)}`);
    }
    return intervalGroups.map((interval) => {
      const intervalMin = parseFloat(interval.split('-')[0]);
      const dummy = { vmg_perc_avg: intervalMin };
      return {
        name: interval,
        color: getItemColor(dummy as Record<string, unknown>),
      };
    });
  }

  const { groups, getItemColor } = buildColorGrouping(arr, colorName);
  return groups.map((g) => ({
    name: String(g.key),
    color: g.items.length > 0 ? getItemColor(g.items[0]) : 'lightgrey',
  }));
}
